/**
 * shipping-globe.js — covred theme
 *
 * Globe canvas avec vol continu :
 *   Hô Chi Minh-Ville → Guangzhou (transit) → Pays-Bas (hub) → France
 *   Depuis le hub Pays-Bas, 2 avions secondaires partent vers : Belgique, Suisse
 *
 * Conventions covred (voir sections/CLAUDE.md) :
 *   - rAF pausé quand la section sort du viewport (économie batterie mobile)
 *   - rAF pausé quand l'onglet est inactif
 *   - Cleanup des observers + rAF sur shopify:section:unload (designMode)
 *   - Re-init automatique des nouveaux canvas sur shopify:section:load (designMode)
 */
(function () {
  "use strict";
  if (window.__sgGlobeAsset) return;
  window.__sgGlobeAsset = true;

  // ===== Cities =====
  const HCM = { lng: 106.66, lat: 10.78 };
  const GZ  = { lng: 113.26, lat: 23.13 };
  const NL  = { lng: 4.90,   lat: 52.37 };
  const PA  = { lng: 2.35,   lat: 48.85 };
  const BE  = { lng: 4.35,   lat: 50.85 };
  const CH  = { lng: 7.45,   lat: 46.95 };

  // ===== Timeline (ms) =====
  const T_INTRO    = 500;
  const T_FLIGHT_1 = 1000;
  const T_FLIGHT_2 = 2200;
  const T_FLIGHT_3 = 800;
  const T_OUTRO    = 1500;
  const T_FULL_FLIGHT = T_FLIGHT_1 + T_FLIGHT_2 + T_FLIGHT_3;
  const T_TOTAL = T_INTRO + T_FULL_FLIGHT + T_OUTRO;

  function easeIO(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function easeO(t)  { return 1 - Math.pow(1 - t, 3); }

  const dynImport = new Function("url", "return import(url)");
  let dataPromise = null;
  function loadData() {
    if (dataPromise) return dataPromise;
    dataPromise = Promise.all([
      dynImport("https://cdn.jsdelivr.net/npm/d3-geo@3/+esm"),
      dynImport("https://cdn.jsdelivr.net/npm/topojson-client@3/+esm"),
      fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json").then(r => r.json())
    ]).then(([d3, topo, world]) => {
      try {
        return { d3, land: topo.feature(world, world.objects.land) };
      } catch (e) {
        return null;
      }
    }).catch(e => null);
    return dataPromise;
  }

  // Cleanups par canvas, pour le shopify:section:unload
  const cleanups = new Map();

  function initCanvas(canvas) {
    if (canvas.dataset.sgInit) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 20) {
      setTimeout(() => initCanvas(canvas), 200);
      return;
    }
    canvas.dataset.sgInit = "1";

    loadData().then(data => {
      if (!data) {
        canvas.style.background = "radial-gradient(circle at 35% 30%, #0c1610, #040a07 55%, #000301)";
        canvas.style.borderRadius = "50%";
        canvas.classList.add("is-ready");
        return;
      }

      const { d3, land } = data;
      const ctx = canvas.getContext("2d");
      const dpr = Math.max(2, Math.min(window.devicePixelRatio || 1, 4));
      let cssW, cssH, cx, cy, BASE_R;
      const projection = d3.geoOrthographic().clipAngle(90).precision(0.3);
      const path = d3.geoPath(projection, ctx);

      function resize() {
        cssW = canvas.offsetWidth || 380;
        cssH = canvas.offsetHeight || cssW;
        canvas.width = cssW * dpr;
        canvas.height = cssH * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        cx = cssW / 2;
        cy = cssH / 2;
        BASE_R = Math.min(cx, cy) - 4;
        projection.translate([cx, cy]);
      }
      resize();
      window.addEventListener("resize", resize);

      const planeSilh = new Path2D("M 12 0 L 4 -1.8 L 2 -2 L -2 -12 L -5 -12 L -3 -2 L -7 -1.8 L -8 -4.5 L -11 -4.5 L -10 -1 L -12 0 L -10 1 L -11 4.5 L -8 4.5 L -7 1.8 L -3 2 L -5 12 L -2 12 L 2 2 L 4 1.8 Z");

      const route1 = d3.geoInterpolate([HCM.lng, HCM.lat], [GZ.lng, GZ.lat]);
      const route2 = d3.geoInterpolate([GZ.lng, GZ.lat], [NL.lng, NL.lat]);
      const route3 = d3.geoInterpolate([NL.lng, NL.lat], [PA.lng, PA.lat]);
      const routeNL_BE = d3.geoInterpolate([NL.lng, NL.lat], [BE.lng, BE.lat]);
      const routeNL_CH = d3.geoInterpolate([NL.lng, NL.lat], [CH.lng, CH.lat]);

      let camLng = HCM.lng, camLat = HCM.lat, camZoom = 4;
      let planeSeg = 1;
      let planeP   = 0;
      let popupHCM = 0;
      let popupGZ  = 0;
      let popupNL  = 0;
      let popupPA  = 0;
      let popupBE  = 0;
      let popupCH  = 0;
      let trail1Op = 0, trail2Op = 0, trail3Op = 0;

      function isFront(lng, lat) {
        const rot = projection.rotate();
        return d3.geoDistance([lng, lat], [-rot[0], -rot[1]]) < Math.PI / 2 - 0.02;
      }

      function drawMarker(lng, lat, color, r) {
        if (!isFront(lng, lat)) return;
        const p = projection([lng, lat]);
        if (!p) return;
        const [x, y] = p;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
        g.addColorStop(0, color);
        g.addColorStop(0.4, color.replace(/[\d.]+\)$/, "0.4)"));
        g.addColorStop(1, color.replace(/[\d.]+\)$/, "0)"));
        ctx.beginPath();
        ctx.arc(x, y, r * 4, 0, Math.PI * 2);
        ctx.fillStyle = g; ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = color; ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, r + 1.5, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      function drawTrail(routeFn, progress, opacity, thin) {
        if (progress <= 0 || opacity <= 0) return;
        const N = thin ? 30 : 56;
        const end = Math.max(1, Math.floor(N * progress));
        const coords = [];
        for (let i = 0; i <= end; i++) {
          coords.push(routeFn(Math.min(progress, i / N)));
        }
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.shadowBlur = thin ? 14 : 20;
        ctx.shadowColor = "rgba(0,255,136,0.9)";
        ctx.lineWidth = thin ? 1.8 : 2.4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = thin ? "rgba(0,255,136,0.6)" : "rgba(0,255,136,0.55)";
        ctx.beginPath();
        path({ type: "LineString", coordinates: coords });
        ctx.stroke();
        ctx.restore();
        if (thin) return;
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 1.5;
        const s = projection(coords[0]);
        const e = projection(coords[coords.length - 1]);
        if (s && e) {
          const grd = ctx.createLinearGradient(s[0], s[1], e[0], e[1]);
          grd.addColorStop(0, "rgba(245,250,255,0)");
          grd.addColorStop(0.55, "rgba(245,250,255,0.35)");
          grd.addColorStop(1, "rgba(255,255,255,0.95)");
          ctx.strokeStyle = grd;
        } else {
          ctx.strokeStyle = "rgba(245,250,255,0.65)";
        }
        ctx.beginPath();
        path({ type: "LineString", coordinates: coords });
        ctx.stroke();
        ctx.restore();
      }

      function getBearing(routeFn, p) {
        const eps = 0.005;
        const a = projection(routeFn(Math.max(0, p - eps)));
        const b = projection(routeFn(Math.min(1, p + eps)));
        if (!a || !b) return 0;
        return Math.atan2(b[1] - a[1], b[0] - a[0]);
      }

      function drawPlane(routeFn, p, scale, opacity) {
        scale = scale == null ? 1 : scale;
        opacity = opacity == null ? 1 : opacity;
        const pos = routeFn(p);
        if (!isFront(pos[0], pos[1])) return;
        const proj = projection(pos);
        if (!proj) return;
        const b = getBearing(routeFn, p);
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.translate(proj[0], proj[1]);
        ctx.rotate(b);
        if (scale !== 1) ctx.scale(scale, scale);
        ctx.shadowBlur = 14 * scale;
        ctx.shadowColor = "rgba(167,243,208,0.95)";
        ctx.fillStyle = "#fff";
        ctx.fill(planeSilh);
        ctx.shadowBlur = 0;
        ctx.fill(planeSilh);
        ctx.strokeStyle = "rgba(0,255,136,0.7)";
        ctx.lineWidth = 0.7;
        ctx.lineJoin = "round";
        ctx.stroke(planeSilh);
        ctx.restore();
      }

      function drawPopup(lng, lat, text, bg, glow, op, sc) {
        if (op <= 0) return;
        if (!isFront(lng, lat)) return;
        const p = projection([lng, lat]);
        if (!p) return;
        const ax = p[0];
        const ay = p[1] - 16;
        ctx.save();
        ctx.globalAlpha = op;
        if (sc !== 1) {
          ctx.translate(ax, ay);
          ctx.scale(sc, sc);
          ctx.translate(-ax, -ay);
        }
        ctx.font = '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
        const m = ctx.measureText(text);
        const pw = Math.ceil(m.width) + 26;
        const ph = 26;
        const px = ax - pw / 2;
        const py = ay - ph - 8;
        const r = ph / 2;
        function pill() {
          ctx.beginPath();
          ctx.moveTo(px + r, py);
          ctx.lineTo(px + pw - r, py);
          ctx.arcTo(px + pw, py, px + pw, py + r, r);
          ctx.lineTo(px + pw, py + ph - r);
          ctx.arcTo(px + pw, py + ph, px + pw - r, py + ph, r);
          ctx.lineTo(px + r, py + ph);
          ctx.arcTo(px, py + ph, px, py + ph - r, r);
          ctx.lineTo(px, py + r);
          ctx.arcTo(px, py, px + r, py, r);
          ctx.closePath();
        }
        ctx.shadowBlur = 18;
        ctx.shadowColor = glow;
        pill();
        ctx.fillStyle = bg; ctx.fill();
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.moveTo(ax - 5, py + ph - 0.5);
        ctx.lineTo(ax, py + ph + 6);
        ctx.lineTo(ax + 5, py + ph - 0.5);
        ctx.closePath();
        ctx.fillStyle = bg; ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.22)";
        ctx.lineWidth = 1;
        pill(); ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, px + pw / 2, py + ph / 2 + 0.5);
        ctx.restore();
      }

      function render() {
        const dispW = canvas.offsetWidth;
        if (dispW && Math.abs(dispW - cssW) > 0.5) resize();
        projection.rotate([-camLng, -camLat]).scale(BASE_R * camZoom);
        ctx.clearRect(0, 0, cssW, cssH);
        const visR = Math.min(BASE_R * camZoom, Math.min(cssW, cssH) / 2 - 3);

        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, visR, 0, Math.PI * 2);
        ctx.clip();

        ctx.beginPath();
        path({ type: "Sphere" });
        const gFill = ctx.createRadialGradient(cx, cy, 0, cx, cy, BASE_R * camZoom);
        gFill.addColorStop(0, "rgba(12, 22, 16, 0.95)");
        gFill.addColorStop(0.75, "rgba(4, 10, 7, 0.92)");
        gFill.addColorStop(1, "rgba(0, 3, 1, 0.85)");
        ctx.fillStyle = gFill;
        ctx.fill();

        ctx.beginPath();
        path(land);
        ctx.fillStyle = "rgba(35, 70, 50, 0.85)";
        ctx.fill();

        drawMarker(HCM.lng, HCM.lat, "rgba(251, 191, 36, 1)", 4);
        drawMarker(GZ.lng,  GZ.lat,  "rgba(255, 255, 255, 1)", 3);
        drawMarker(NL.lng,  NL.lat,  "rgba(255, 255, 255, 1)", 3);
        drawMarker(PA.lng,  PA.lat,  "rgba(236, 72, 153, 1)", 4);
        drawMarker(BE.lng,  BE.lat,  "rgba(236, 72, 153, 1)", 4);
        drawMarker(CH.lng,  CH.lat,  "rgba(236, 72, 153, 1)", 4);

        drawTrail(route1, planeSeg >= 2 ? 1 : planeP, trail1Op, false);
        if (planeSeg >= 2) drawTrail(route2, planeSeg >= 3 ? 1 : planeP, trail2Op, false);
        if (planeSeg >= 3) drawTrail(route3, planeP, trail3Op, false);

        if (planeSeg >= 3) {
          drawTrail(routeNL_BE, planeP, trail3Op, true);
          drawTrail(routeNL_CH, planeP, trail3Op, true);
        }

        const mainRoute = planeSeg === 1 ? route1 : planeSeg === 2 ? route2 : route3;
        drawPlane(mainRoute, planeP, 1, 1);

        if (planeSeg >= 3) {
          drawPlane(routeNL_BE, planeP, 0.75, 0.9);
          drawPlane(routeNL_CH, planeP, 0.75, 0.9);
        }

        drawPopup(HCM.lng, HCM.lat, "🇻🇳 Hô Chi Minh-Ville",        "rgba(0,153,204,0.95)",  "rgba(0,200,255,0.85)", popupHCM, 0.85 + 0.15 * popupHCM);
        drawPopup(GZ.lng,  GZ.lat,  "🇨🇳 Guangzhou (transit)",      "rgba(0,153,204,0.95)",  "rgba(0,200,255,0.85)", popupGZ,  0.85 + 0.15 * popupGZ);
        drawPopup(NL.lng,  NL.lat,  "🇳🇱 Pays-Bas (hub Europe)",    "rgba(0,153,204,0.95)",  "rgba(0,200,255,0.85)", popupNL,  0.85 + 0.15 * popupNL);
        drawPopup(PA.lng,  PA.lat,  "🇫🇷 France",                   "rgba(16,185,129,0.96)", "rgba(16,185,129,0.9)", popupPA,  0.82 + 0.18 * popupPA);
        drawPopup(BE.lng,  BE.lat,  "🇧🇪 Belgique",                "rgba(16,185,129,0.96)", "rgba(16,185,129,0.9)", popupBE,  0.82 + 0.18 * popupBE);
        drawPopup(CH.lng,  CH.lat,  "🇨🇭 Suisse",                  "rgba(16,185,129,0.96)", "rgba(16,185,129,0.9)", popupCH,  0.82 + 0.18 * popupCH);

        ctx.restore();

        ctx.save();
        ctx.shadowBlur = 14;
        ctx.shadowColor = "rgba(0, 255, 136, 0.45)";
        ctx.beginPath();
        ctx.arc(cx, cy, visR, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(0, 255, 136, 0.75)";
        ctx.lineWidth = 1.4;
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.beginPath();
        ctx.arc(cx, cy, visR - 1.2, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(167, 243, 208, 0.22)";
        ctx.lineWidth = 0.6;
        ctx.stroke();
        ctx.restore();
      }

      const REGION_LNG = (NL.lng + BE.lng + CH.lng + PA.lng) / 4;
      const REGION_LAT = (NL.lat + BE.lat + CH.lat + PA.lat) / 4;

      // ===== rAF loop + viewport / visibility pause =====
      let raf = null;
      let cycleStart = 0;
      let inViewport = true;

      /* [ANNOTATION | DATE: 2026-05-13]
         WHY: pause le rAF du globe quand la section n'est pas dans le viewport.
         Sinon le canvas tourne en permanence (50-60fps) même quand l'utilisateur est ailleurs,
         ce qui draine la batterie mobile et consomme le CPU pour rien.
         Ref: sections/CLAUDE.md (Animations rAF — obligatoire pour les sections animées).
         IMPACT: globe se met en pause hors viewport et reprend dès qu'on remonte. */
      const driftObs = new IntersectionObserver((entries) => {
        for (const e of entries) inViewport = e.isIntersecting;
        if (inViewport && raf == null) startCycle();
      }, { root: null, rootMargin: '100px 0px 100px 0px', threshold: 0 });
      driftObs.observe(canvas);

      function onVisibilityChange() {
        if (document.hidden) {
          if (raf) { cancelAnimationFrame(raf); raf = null; }
        } else if (inViewport) {
          startCycle();
        }
      }
      document.addEventListener('visibilitychange', onVisibilityChange);

      function startCycle() {
        if (raf != null) return;
        cycleStart = performance.now();
        raf = requestAnimationFrame(tick);
      }

      function tick(now) {
        if (!inViewport || document.hidden) { raf = null; return; }

        const t = now - cycleStart;
        if (t >= T_TOTAL) {
          // Boucle avec une pause de 800ms
          raf = null;
          setTimeout(() => { if (inViewport && !document.hidden) startCycle(); }, 800);
          return;
        }

        const eIntro = T_INTRO;
        const eFlight = eIntro + T_FULL_FLIGHT;
        const f1 = T_FLIGHT_1 / T_FULL_FLIGHT;
        const f12 = (T_FLIGHT_1 + T_FLIGHT_2) / T_FULL_FLIGHT;
        const POPUP_HALF_WIDTH = 0.08;

        if (t < eIntro) {
          camLng = HCM.lng; camLat = HCM.lat;
          camZoom = 4;
          planeSeg = 1; planeP = 0;
          popupHCM = t < 250 ? easeO(t / 250) : 1;
          popupGZ = 0; popupNL = 0; popupPA = 0; popupBE = 0; popupCH = 0;
          trail1Op = 0; trail2Op = 0; trail3Op = 0;
        } else if (t < eFlight) {
          const tIn = t - eIntro;
          const rawP = tIn / T_FULL_FLIGHT;
          const globalP = rawP;
          if (globalP <= f1) {
            planeSeg = 1; planeP = globalP / f1;
          } else if (globalP <= f12) {
            planeSeg = 2; planeP = (globalP - f1) / (f12 - f1);
          } else {
            planeSeg = 3; planeP = (globalP - f12) / (1 - f12);
          }
          const routeFn = planeSeg === 1 ? route1 : planeSeg === 2 ? route2 : route3;
          const camPos = routeFn(planeP);
          camLng = camPos[0]; camLat = camPos[1];
          camZoom = 4 - 3 * Math.sin(Math.PI * rawP);
          popupHCM = Math.max(0, 1 - easeO(Math.min(1, rawP / 0.12)));
          const dGZ = Math.abs(globalP - f1) / POPUP_HALF_WIDTH;
          popupGZ = dGZ < 1 ? (1 - dGZ * dGZ) : 0;
          const dNL = Math.abs(globalP - f12) / POPUP_HALF_WIDTH;
          popupNL = dNL < 1 ? (1 - dNL * dNL) : 0;
          popupPA = 0; popupBE = 0; popupCH = 0;
          if (planeSeg === 1) {
            trail1Op = 1; trail2Op = 0; trail3Op = 0;
          } else if (planeSeg === 2) {
            trail1Op = 0.55; trail2Op = 1; trail3Op = 0;
          } else {
            trail1Op = 0.3; trail2Op = 0.55; trail3Op = 1;
          }
        } else {
          const tIn = t - eFlight;
          const outroP = Math.min(1, tIn / T_OUTRO);
          camZoom = 4 + 1.5 * easeIO(outroP);
          camLng = PA.lng + (REGION_LNG - PA.lng) * easeIO(outroP);
          camLat = PA.lat + (REGION_LAT - PA.lat) * easeIO(outroP);
          planeSeg = 3; planeP = 1;
          popupHCM = 0; popupGZ = 0; popupNL = 0;
          popupPA = Math.min(1, easeO(tIn / 350));
          popupBE = tIn > 250 ? Math.min(1, easeO((tIn - 250) / 350)) : 0;
          popupCH = tIn > 500 ? Math.min(1, easeO((tIn - 500) / 350)) : 0;
          trail1Op = 0.3; trail2Op = 0.55; trail3Op = 1;
        }

        render();
        raf = requestAnimationFrame(tick);
      }

      // Cleanup function for shopify:section:unload
      cleanups.set(canvas, () => {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        try { driftObs.disconnect(); } catch (e) {}
        document.removeEventListener('visibilitychange', onVisibilityChange);
      });

      canvas.classList.add("is-ready");
      render();
      setTimeout(startCycle, 200);
    });
  }

  function initAll() {
    document.querySelectorAll(".sg-globe-canvas:not([data-sg-init])").forEach(initCanvas);
  }

  // Initial run
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    requestAnimationFrame(initAll);
  }

  // Designmode : sections rechargées dynamiquement par l'éditeur Shopify
  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:unload", (evt) => {
    const target = evt.target;
    if (!target || !target.querySelectorAll) return;
    target.querySelectorAll(".sg-globe-canvas").forEach((c) => {
      const fn = cleanups.get(c);
      if (fn) {
        try { fn(); } catch (e) {}
        cleanups.delete(c);
      }
    });
  });
})();
